import React, { useContext, useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { UI, ScreenSize, DropdownItem } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { useTranslation } from "react-i18next";
import { AccessRow } from "src/stores/UserAccessStore";

interface RealestateAccessListProps {
    listIdentifier: string;
}

interface ListSelectionArgs {
    searchQuery?: string;
    filterItemValue?: string;
    sortItemValue?: string;
}

const desktopColumns = {
    name: 0.28,
    number: 0.14,
    roles: 0.22,
    condominium: 0.12,
    deleted: 0.12,
    mandant: 0.12
};

const RealestateAccessListBase = (props: RealestateAccessListProps) => {
    const { uiStore, navStore, userAccessStore, tenantAccessStore } = useContext(RootStoreContext);
    const { t } = useTranslation();

    if (!navStore.listMap.has(props.listIdentifier)) {
        navStore.setListMapValue(props.listIdentifier, {});
    }

    const listParameters = navStore.getListMapValue(props.listIdentifier);

    let currentSearchQuery: string | undefined = undefined;

    if (listParameters && listParameters.searchQuery) {
        currentSearchQuery = listParameters.searchQuery;
    }

    let currentRoleFilter: string | undefined = undefined;
    if (listParameters && listParameters.sortValue) {
        currentRoleFilter = listParameters.sortValue;
    }

    const [searchQuery, setSearchQuery] = useState(currentSearchQuery ?? "");
    const [selectedRole, setSelectedRole] = useState(currentRoleFilter ?? "all");

    const persistFilters = useCallback(
        (nextSearchQuery: string | undefined, nextRoleFilter: string) => {
            navStore.setListMapValue(props.listIdentifier, {
                ...(listParameters ?? {}),
                searchQuery: nextSearchQuery,
                sortValue: nextRoleFilter,
                paginationPage: 1
            });
        },
        [navStore, listParameters, props.listIdentifier]
    );

    const roleFilterOptions = useMemo<DropdownItem[]>(() => {
        const roleIdsInRows = new Set<number>();
        userAccessStore.accessRows.forEach((row) => {
            tenantAccessStore.getRoleIds(row.role).forEach((roleId) => roleIdsInRows.add(roleId));
        });

        const roleItems = Array.from(roleIdsInRows)
            .map((roleId) => ({
                value: String(roleId),
                label: userAccessStore.roleLabelsById.get(roleId) ?? String(roleId)
            }))
            .sort((firstRole, secondRole) => firstRole.label.localeCompare(secondRole.label));

        return [{ value: "all", label: t("screens.dashboard.users.access.all_roles") }, ...roleItems];
    }, [tenantAccessStore, userAccessStore.accessRows, userAccessStore.roleLabelsById, t]);

    const selectedRoleItem = useMemo(
        () => roleFilterOptions.find((option) => option.value === selectedRole) ?? roleFilterOptions[0],
        [roleFilterOptions, selectedRole]
    );
    const effectiveSelectedRole = useMemo(
        () => (roleFilterOptions.some((option) => option.value === selectedRole) ? selectedRole : "all"),
        [roleFilterOptions, selectedRole]
    );

    const filteredAccessRows = useMemo(() => {
        const normalizedQuery = searchQuery.toLowerCase().trim();

        return userAccessStore.accessRows.filter((row) => {
            const roleIds = tenantAccessStore.getRoleIds(row.role);
            const rolesLabel =
                roleIds.length > 0
                    ? roleIds.map((roleId) => userAccessStore.roleLabelsById.get(roleId) ?? String(roleId)).join(", ")
                    : "";
            const searchText = `${row.realestatename ?? ""} ${row.realestatenumber ?? ""} ${rolesLabel}`
                .toLowerCase()
                .trim();
            const queryMatches = normalizedQuery.length === 0 || searchText.includes(normalizedQuery);
            const roleMatches = tenantAccessStore.matchesRoleFilter(row.role, effectiveSelectedRole);
            return queryMatches && roleMatches;
        });
    }, [searchQuery, effectiveSelectedRole, tenantAccessStore, userAccessStore.accessRows, userAccessStore.roleLabelsById]);

    const renderItem = useCallback(
        (row: AccessRow, screenSize: ScreenSize) => {
            const nameLabel = row.realestatename ?? "—";
            const numberLabel = row.realestatenumber ?? "—";
            const rolesLabel =
                row.role && row.role.length > 0
                    ? row.role.map((roleId) => userAccessStore.roleLabelsById.get(roleId) ?? String(roleId)).join(", ")
                    : "—";
            const condominiumLabel =
                row.condominium === null || row.condominium === undefined
                    ? "—"
                    : row.condominium
                    ? t("labels.yes")
                    : t("labels.no");
            const deletedLabel =
                row.deleted === null || row.deleted === undefined
                    ? "—"
                    : row.deleted
                    ? t("labels.yes")
                    : t("labels.no");
            const consolidationLabel =
                row.consolidation === null || row.consolidation === undefined
                    ? "—"
                    : row.consolidation
                    ? t("labels.yes")
                    : t("labels.no");

            let listRows: React.ReactElement[] = [];

            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    listRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell key={"c-1"} colspan={desktopColumns.name} value={nameLabel} />
                            <UI.List.Cell key={"c-2"} colspan={desktopColumns.number} value={numberLabel} />
                            <UI.List.Cell key={"c-3"} colspan={desktopColumns.roles} value={rolesLabel} />
                            <UI.List.Cell key={"c-4"} colspan={desktopColumns.condominium} value={condominiumLabel} />
                            <UI.List.Cell key={"c-5"} colspan={desktopColumns.deleted} value={deletedLabel} />
                            <UI.List.Cell key={"c-6"} colspan={desktopColumns.mandant} value={consolidationLabel} />
                        </UI.List.Row>
                    ];
                    break;

                case ScreenSize.MOBILE: {
                    const compactName = row.realestatenumber ? `${nameLabel} (${row.realestatenumber})` : nameLabel;
                    listRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell key={"c-1"} colspan={0.6} value={compactName} />
                            <UI.List.Cell key={"c-2"} colspan={0.4} value={rolesLabel} />
                        </UI.List.Row>
                    ];
                    break;
                }
            }

            return <UI.List.Item key={row.realestateid} screenSize={screenSize} rows={listRows} />;
        },
        [userAccessStore.roleLabelsById, t]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            let headerRows: React.ReactElement[] = [];

            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    headerRows = [
                        <UI.List.Row key={"row-1"}>
                            <UI.List.Cell key={"c-1"} colspan={desktopColumns.name} value={t("labels.object")} />
                            <UI.List.Cell key={"c-2"} colspan={desktopColumns.number} value={t("labels.number")} />
                            <UI.List.Cell key={"c-3"} colspan={desktopColumns.roles} value={t("labels.roles")} />
                            <UI.List.Cell
                                key={"c-4"}
                                colspan={desktopColumns.condominium}
                                value={t("labels.condominium")}
                            />
                            <UI.List.Cell key={"c-5"} colspan={desktopColumns.deleted} value={t("labels.deleted")} />
                            <UI.List.Cell key={"c-6"} colspan={desktopColumns.mandant} value={t("labels.mandant")} />
                        </UI.List.Row>
                    ];
                    break;
                case ScreenSize.MOBILE:
                    headerRows = [
                        <UI.List.Row key={"row-1"}>
                            <UI.List.Cell key={"c-1"} colspan={0.6} value={t("labels.object")} />
                            <UI.List.Cell key={"c-2"} colspan={0.4} value={t("labels.roles")} />
                        </UI.List.Row>
                    ];
                    break;
            }

            return <UI.List.Header rows={headerRows} />;
        },
        [t]
    );

    const onPageChanged = useCallback(
        (currentPage: number) => {
            navStore.setListMapValue(
                props.listIdentifier,
                listParameters
                    ? { ...listParameters, scrollPosition: window.pageYOffset, paginationPage: currentPage }
                    : { scrollPosition: window.pageYOffset, paginationPage: currentPage }
            );
        },
        [navStore, listParameters, props.listIdentifier]
    );

    const handleSearchInput = useCallback(
        ({ searchQuery: nextSearchQuery, sortItemValue }: ListSelectionArgs) => {
            const nextRole = sortItemValue ?? effectiveSelectedRole;
            setSearchQuery(nextSearchQuery ?? "");
            setSelectedRole(nextRole);
            persistFilters(nextSearchQuery, nextRole);
        },
        [persistFilters, effectiveSelectedRole]
    );

    const onChangeSort = useCallback(
        ({ searchQuery: nextSearchQuery, sortItemValue }: ListSelectionArgs) => {
            const nextRole = sortItemValue ?? "all";
            const resolvedSearchQuery = nextSearchQuery ?? searchQuery;
            setSelectedRole(nextRole);
            setSearchQuery(resolvedSearchQuery ?? "");
            persistFilters(resolvedSearchQuery, nextRole);
        },
        [persistFilters, searchQuery]
    );

    return (
        <UI.List.BasicList
            screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
            items={filteredAccessRows}
            renderItem={renderItem}
            renderHeader={renderHeader}
            scrollPosition={listParameters?.scrollPosition ?? undefined}
            itemsPerPage={12}
            initialPage={listParameters?.paginationPage ?? undefined}
            onPageChanged={onPageChanged}
            onSearch={handleSearchInput}
            initialSearchQuery={currentSearchQuery}
            sortItems={roleFilterOptions}
            initialSortItem={selectedRoleItem}
            onChangeSort={onChangeSort}
        />
    );
};

export const RealestateAccessList = observer(RealestateAccessListBase);
